import { FC, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { convertObjectFieldsToVariables } from '@libraryHelpers/typeAdapters'
import { ScriptValueDialog, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import { COMMON_SCRIPT_VARIABLES } from '@mobileConstants'
import { MobileListItem } from '@mobileTypes'
import { Code as CodeIcon } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'

import { useFetchAllDisplayRulesQuery } from '@redux/api/displayRules.api'
import { useFetchMultipleObjectsByCodeQuery } from '@redux/api/objects.api'

import { FIELD_VALUE_TYPE, FORMATS_DATE_OPTIONS } from '@constants'
import { ObjectFieldDTO, ShortDisplayRule } from '@types'
import {
  ModalRefPathPicker,
  type RefPathValue,
} from '@libraryComponents/ModalRefPathPicker/ModalRefPathPicker'

interface FieldConfigDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialData?: Partial<FieldConfigFormData>
  commands: Array<{
    commandKey: string
    commandName: string
    objectCode: string
    objectTitle: string
  }>
  dataSource?: MobileListItem['dataSource']
  onClose: () => void
  onSave: (config: FieldConfig) => void
}

export interface FieldConfig {
  title: string
  bindType: 'field' | 'js'
  useDisplayRule: boolean
  displayRule: ShortDisplayRule | null
  // Для bindType = 'field'
  commandKey?: string
  fieldName?: string
  pathArray?: RefPathValue['pathArray'] | null
  formatDate?: string | null
  // Для bindType = 'js'
  jsCode?: string
}

interface FieldConfigFormData {
  title: string
  bindType: 'field' | 'js'
  useDisplayRule: boolean
  displayRule: ShortDisplayRule | null
  command: { key: string; name: string; objectCode: string; objectTitle: string } | null
  field: (ObjectFieldDTO & { format?: any }) | null
  pathArray: RefPathValue['pathArray'] | null
  jsCode: string
  formatDate: string | null
}

export const FieldConfigDialog: FC<FieldConfigDialogProps> = ({
  open,
  mode,
  initialData,
  commands,
  dataSource,
  onClose,
  onSave,
}) => {
  const [showJsEditor, setShowJsEditor] = useState(false)

  // Проверяем, зафиксирована ли команда через DataSource
  const isCommandFixed = 
    (dataSource?.bindType === 'command' && !!dataSource.commandKey) ||
    (dataSource?.bindType === 'prefilled' && !!dataSource.prefilledVariable)
  
  const fixedCommand = useMemo(() => {
    if (dataSource?.bindType === 'command' && dataSource.commandKey) {
      return commands.find(c => c.commandKey === dataSource.commandKey) || null
    }
    if (dataSource?.bindType === 'prefilled' && dataSource.prefilledVariable) {
      return commands.find(c => c.commandKey === 'prefilled') || null
    }
    return null
  }, [dataSource, commands])

  const { control, handleSubmit, reset, watch, setValue } = useForm<FieldConfigFormData>({
    defaultValues: {
      title: '',
      bindType: 'field',
      useDisplayRule: false,
      displayRule: null,
      command: null,
      field: null,
      pathArray: null,
      jsCode: '',
      formatDate: null,
    },
  })

  const watchBindType = watch('bindType')
  const watchUseDisplayRule = watch('useDisplayRule')
  const watchCommand = watch('command')
  const watchField = watch('field')
  const watchPathArray = watch('pathArray')
  const watchJsCode = watch('jsCode')
  const watchFormatDate = watch('formatDate')

  const isDateTimeField =
    watchBindType === 'field' && watchField?.valueType === FIELD_VALUE_TYPE.DATETIME

  useEffect(() => {
    if (!isDateTimeField && watchFormatDate !== null) {
      setValue('formatDate', null)
    }
  }, [isDateTimeField, watchFormatDate, setValue])

  // Загружаем объекты для всех команд
  const objectCodes = useMemo(() => commands.map(c => c.objectCode), [commands])
  const { data: objectModels = [] } = useFetchMultipleObjectsByCodeQuery(objectCodes, {
    skip: objectCodes.length === 0,
  })

  const commandVariables = useMemo(
    () =>
      commands.reduce<
        Array<{
          name: string
          type: ReturnType<typeof convertObjectFieldsToVariables>
          isArray: boolean
          description?: string
        }>
      >((acc, command) => {
        const objectModel = objectModels.find(model => model.code === command.objectCode)

        if (objectModel?.fields) {
          acc.push({
            name: command.commandKey,
            type: convertObjectFieldsToVariables(objectModel.fields),
            isArray: true,
            description: command.objectCode,
          })
        }

        return acc
      }, []),
    [commands, objectModels]
  )

  const fieldConfigScriptVariables = useMemo(
    () => [
      ...commandVariables,
      { name: 'item', type: 'any', description: 'Current item from the data source' },
      { name: 'list', type: 'any', isArray: true, description: 'Array of all items from the data source' },
      ...COMMON_SCRIPT_VARIABLES,
    ],
    [commandVariables]
  )

  // Опции для селектора команд
  const commandOptions = useMemo(() => {
    return commands.map(cmd => ({
      key: cmd.commandKey,
      name: cmd.commandName,
      objectCode: cmd.objectCode,
      objectTitle: cmd.objectTitle,
    }))
  }, [commands])

  // Опции для селектора полей (зависит от выбранной команды)
  const fieldOptions = useMemo(() => {
    if (!watchCommand) return []
    const objectModel = objectModels.find(model => model.code === watchCommand.objectCode)

    return objectModel?.fields || []
  }, [watchCommand, objectModels])

  // Загружаем все displayRules (без фильтрации по objectCode и objectField)
  const { data: displayRulesData, isLoading: isLoadingDisplayRules } = useFetchAllDisplayRulesQuery(
    {
      objectCode: undefined,
      objectField: undefined,
    },
    {
      skip: !watchUseDisplayRule,
      refetchOnMountOrArgChange: true,
    }
  )

  const displayRuleOptions = useMemo(() => {
    // Клонируем, чтобы не мутировать данные из кэша (RTK Query может отдавать неизменяемые объекты)
    const options: ShortDisplayRule[] = displayRulesData ? [...displayRulesData] : []

    // Добавляем displayRule из watch(), если редактируем и он не в списке
    const currentDisplayRule = watch('displayRule')
    if (currentDisplayRule && currentDisplayRule.id) {
      const existsInOptions = options.some(opt => opt.id === currentDisplayRule.id)
      if (!existsInOptions) {
        options.unshift(currentDisplayRule)
      }
    }

    // Также добавляем displayRule из initialData, если его нет в загруженных опциях
    if (initialData?.displayRule && initialData.displayRule.id) {
      const existsInOptions = options.some(opt => opt.id === initialData.displayRule.id)
      if (!existsInOptions && (!currentDisplayRule || currentDisplayRule.id !== initialData.displayRule.id)) {
        options.unshift(initialData.displayRule)
      }
    }

    return options
  }, [displayRulesData, initialData?.displayRule, watch])

  // Инициализируем форму
  useEffect(() => {
    if (open) {
      if (initialData) {
        // Режим edit или предзаполнение
        const pathArray = (initialData as any).pathArray || null
        reset({
          title: initialData.title || '',
          bindType: initialData.bindType || 'field',
          useDisplayRule: initialData.useDisplayRule || false,
          displayRule: initialData.displayRule || null,
          command: initialData.command || fixedCommand || null,
          field: (initialData.field as FieldConfigFormData['field']) ?? null,
          pathArray: pathArray,
          jsCode: initialData.jsCode || '',
          formatDate: initialData.formatDate ?? null,
        })
        
        // Восстанавливаем field из pathArray, если field не установлен, но pathArray есть
        if (pathArray && pathArray.length > 0 && !initialData.field) {
          const lastPath = pathArray[pathArray.length - 1]
          if (lastPath?.field) {
            // Найдем поле после загрузки fieldOptions
            setTimeout(() => {
              const foundField = fieldOptions.find(f => f.name === lastPath.field)
              if (foundField) {
                setValue('field', foundField)
              }
            }, 0)
          }
        }
      } else {
        // Режим create с пустыми полями
        reset({
          title: '',
          bindType: 'field',
          useDisplayRule: false,
          displayRule: null,
          command: fixedCommand || null,
          field: null,
          pathArray: null,
          jsCode: '',
          formatDate: null,
        })
      }
    }
  }, [open, initialData, reset, fixedCommand, fieldOptions, setValue])

  // Сбрасываем displayRule если отключили useDisplayRule
  useEffect(() => {
    if (!watchUseDisplayRule) {
      setValue('displayRule', null)
    }
  }, [watchUseDisplayRule, setValue])

  // Сбрасываем поле при смене команды
  useEffect(() => {
    if (watchCommand && watchField) {
      // Проверяем, что текущее поле принадлежит выбранной команде
      const isFieldValid = fieldOptions.some(f => f.name === watchField.name)
      if (!isFieldValid) {
        setValue('field', null)
        setValue('pathArray', null)
      }
    }
  }, [watchCommand, fieldOptions, watchField, setValue])

  const onSubmit = (data: FieldConfigFormData, e?: React.BaseSyntheticEvent) => {
    e?.preventDefault()
    e?.stopPropagation()

    const config: FieldConfig = {
      title: data.title,
      bindType: data.bindType,
      useDisplayRule: data.useDisplayRule,
      displayRule: data.displayRule,
    }

    if (data.bindType === 'field') {
      config.commandKey = data.command?.commandKey || data.command?.key
      config.fieldName = data.field?.name
      config.pathArray = data.pathArray
      config.formatDate = data.formatDate || null
    } else {
      config.jsCode = data.jsCode
      config.formatDate = null
    }

    onSave(config)
    handleClose()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSaveJsCode = (jsCode: string) => {
    setValue('jsCode', jsCode)
    setShowJsEditor(false)
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>{mode === 'edit' ? 'Edit Field' : 'Configure Field'}</DialogTitle>
        <form
          onSubmit={e => {
            e.preventDefault()
            e.stopPropagation()
            handleSubmit(onSubmit)(e)
          }}
        >
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Title Field */}
              <Controller
                name='title'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Display Title'
                    fullWidth
                    helperText='Optional: Title to display in the list (can be empty)'
                    autoFocus
                  />
                )}
              />

              {/* BindType Selector */}
              <Controller
                name='bindType'
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Bind Type</InputLabel>
                    <Select {...field} label='Bind Type'>
                      <MenuItem value='field'>Field</MenuItem>
                      <MenuItem value='js'>JavaScript</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              {/* FIELD MODE */}
              {watchBindType === 'field' && (
                <>
                  {/* Command Selector */}
                  <Controller
                    name='command'
                    control={control}
                    rules={{ required: 'Object is required' }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        {...field}
                        options={commandOptions}
                        getOptionLabel={option => option.objectTitle}
                        isOptionEqualToValue={(option, value) => option.key === value.key}
                        onChange={(_, value) => {
                          field.onChange(value)
                          // Сбрасываем pathArray и field при смене команды
                          if (value?.key !== field.value?.key) {
                            setValue('pathArray', null)
                            setValue('field', null)
                          }
                        }}
                        disabled={isCommandFixed}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label='Object'
                            error={!!fieldState.error}
                            helperText={
                              isCommandFixed
                                ? 'Object is fixed by Data Source configuration'
                                : fieldState.error?.message
                            }
                            required
                          />
                        )}
                      />
                    )}
                  />

                  {/* Field Selector with ModalRefPathPicker */}
                  <Controller
                    name='pathArray'
                    control={control}
                    rules={{ required: 'Field is required' }}
                    render={({ field, fieldState }) => {
                      const displayPath = watchPathArray
                        ? watchPathArray.map(p => p.field).filter(Boolean).join('.')
                        : ''
                      
                      return (
                        <ModalRefPathPicker
                          label='Field'
                          value={
                            watchPathArray && watchPathArray.length > 0
                              ? {
                                  path: displayPath,
                                  pathArray: watchPathArray || [],
                                }
                              : undefined
                          }
                          onChange={next => {
                            field.onChange(next?.pathArray || null)
                            // Синхронизируем field из pathArray
                            if (next?.pathArray && next.pathArray.length > 0) {
                              const lastPath = next.pathArray[next.pathArray.length - 1]
                              if (lastPath?.field) {
                                const foundField = fieldOptions.find(f => f.name === lastPath.field)
                                if (foundField) {
                                  setValue('field', foundField)
                                }
                              }
                            } else {
                              setValue('field', null)
                            }
                          }}
                          hasClearInInput
                          isEdit={!!watchPathArray?.length}
                          isDisabled={!watchCommand}
                          isRequired
                          refPickAllowed
                          object={
                            watchCommand
                              ? { id: watchCommand.objectCode, label: watchCommand.objectTitle }
                              : null
                          }
                          placeholder='Select field from object'
                        />
                      )
                    }}
                  />

                  {isDateTimeField && (
                    <Controller
                      name='formatDate'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label='Date Format'
                          fullWidth
                          value={field.value ?? ''}
                          onChange={event => field.onChange(event.target.value || null)}
                          helperText='Select format for datetime values'
                        >
                          <MenuItem value=''>Default (no formatting)</MenuItem>
                          {FORMATS_DATE_OPTIONS.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  )}
                </>
              )}

              {/* JS MODE */}
              {watchBindType === 'js' && (
                <Box>
                  <Button
                    variant='outlined'
                    fullWidth
                    startIcon={<CodeIcon />}
                    onClick={() => setShowJsEditor(true)}
                  >
                    Edit JavaScript Code
                  </Button>
                </Box>
              )}

              {/* Use Display Rule Checkbox */}

              <>
                <Controller
                  name='useDisplayRule'
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label='Use Display Rule'
                    />
                  )}
                />

                {/* Display Rule Autocomplete */}
                {watchUseDisplayRule && (
                  <Controller
                    name='displayRule'
                    control={control}
                    rules={{
                      required: watchUseDisplayRule
                        ? 'Display Rule is required when enabled'
                        : false,
                    }}
                    render={({ field, fieldState }) => (
                      <Autocomplete
                        {...field}
                        options={displayRuleOptions}
                        getOptionLabel={option =>
                          `${option.title || option.code} (${option.objectCode})`
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        loading={isLoadingDisplayRules}
                        onChange={(_, value) => field.onChange(value)}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label='Display Rule'
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoadingDisplayRules ? (
                                    <CircularProgress color='inherit' size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    )}
                  />
                )}
              </>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type='submit' variant='contained'>
              {mode === 'edit' ? 'Save Changes' : 'Add Field'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ScriptValueDialog для редактирования JS кода */}
      {showJsEditor && (
        <ScriptValueDialog
          isShow
          value={watchJsCode}
          title='Edit JavaScript Code'
          onClose={() => setShowJsEditor(false)}
          onSave={handleSaveJsCode}
          language='js'
          variables={fieldConfigScriptVariables}
          fieldsInfo={variablesToFieldsInfo(fieldConfigScriptVariables)}
          examples={[
            {
              title: 'Current Item Field',
              description: 'Access field from current item',
              code: `return item.fieldName || '-'`,
              category: 'Basic',
            },
            {
              title: 'Item Conditional',
              description: 'Conditional logic with current item',
              code: `if (item.status === 'active') {\n  return 'Active'\n}\nreturn 'Inactive'`,
              category: 'Basic',
            },
            {
              title: 'List Length',
              description: 'Count total items',
              code: `return list.length + ' items'`,
              category: 'List',
            },
            {
              title: 'Find in List',
              description: 'Find specific item in list',
              code: `const found = list.find(x => x.id === item.parentId)\nreturn found?.name || 'Not found'`,
              category: 'List',
            },
            {
              title: 'Command Response',
              description: 'Access another command data',
              code: `return command2[0]?.fieldName || '-'`,
              category: 'Commands',
            },
          ]}
        />
      )}
    </>
  )
}
